import { Alert, Button, Checkbox, FormControlLabel, FormGroup, LinearProgress } from "@mui/material";
import { DialogTitle } from "../Dialog";
import {
	CancellationAcknowledgementText,
	CancellationCheckboxLabel,
	HaltProductionText,
	HangTightCancellationText,
	RequestFailedText,
	SuccessfulCancellationText,
	YouCannotUndoThisActionText
} from "./constants.ts";
import {
	CancellationAcknowledgementWrapper,
	NewOrdersDialogWrapper,
	ProcessingRequestContainer,
	ProgressBarWrapper,
	RequestButtonWrapper,
	RequestModalWrapper,
	RequestSubtitle
} from "./NewOrders.styles.ts";
import { useEffect, useRef, useState } from "react";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import {
	FileSuccessText,
	GenericOrderErrorMaintenanceSubHeader,
	NewOrderErrorMaintenanceHeader
} from "constants/text.ts";
import ErrorBanner from "components/Common/ErrorBanner/ErrorBanner.tsx";
import { isApiErrorResponse } from "features/api/utils/apiUtils.ts";
import { useSelector } from "react-redux";
import { useDialog } from "hooks/useDialog.ts";
import { RootState } from "stores/application.store.tsx";
import { DialogTracking } from "components/Dialog/components/DialogTracking.ts";
import { TypedMutationTrigger } from "@reduxjs/toolkit/query/react";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { SerializedError } from "@reduxjs/toolkit";

interface RequestDialogProps {
	apiTrigger: TypedMutationTrigger<any, any, any>;
	apiError: FetchBaseQueryError | SerializedError | undefined;
	handleAPISuccess: () => void;
	title: string;
	subtitle?: string;
	cancelText: string;
	confirmText: string;
	requestToRemoveText: string;
	resetApi: () => void;
}

const RequestDialog = ({
	apiTrigger,
	apiError,
	handleAPISuccess,
	title,
	subtitle,
	cancelText,
	confirmText,
	requestToRemoveText,
	resetApi
}: RequestDialogProps) => {
	const dialogStatus = useSelector((state: RootState) => state.dialogState.dialogStatus);
	const { closeDialog, dialogId, setDialogAPIInfo, dialogAPIInfo } = useDialog();
	const isLoading = dialogAPIInfo?.isLoadingAPI;
	const isSuccess = dialogAPIInfo?.isSuccessAPI;
	const error = apiError ?? dialogAPIInfo?.apiError;
	const isCancelOrder = dialogStatus === DialogTracking.CANCEL_ORDER;
	const isProcessingRequest = Boolean(isLoading) || Boolean(isSuccess) || Boolean(error);
	const [isCancellingChecked, setIsCancellingChecked] = useState(false);
	const [hasError, setHasError] = useState(false);
	const [localError, setLocalError] = useState<FetchBaseQueryError | SerializedError | undefined>(error);

	const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

	useEffect(() => {
		setLocalError(error);
	}, [error]);

	useEffect(() => {
		return () => {
			clearTimeout(timerRef.current);
		};
	}, []);

	const toggleCancellation = () => {
		setHasError(false);
		setIsCancellingChecked((prevState) => !prevState);
	};

	const handleRequest = async () => {
		setHasError(false);
		setDialogAPIInfo({
			...dialogAPIInfo,
			isLoadingAPI: true
		});
		try {
			await apiTrigger(dialogId ?? "").unwrap();
			setDialogAPIInfo({
				...dialogAPIInfo,
				isLoadingAPI: false,
				isSuccessAPI: true,
				apiError: undefined
			});
			setLocalError(undefined);
			timerRef.current = setTimeout(() => {
				setDialogAPIInfo({
					...dialogAPIInfo,
					isSuccessAPI: false
				});
				handleAPISuccess();
				closeDialog();
			}, 4000);
		} catch {
			console.error(apiError);
			setDialogAPIInfo({
				...dialogAPIInfo,
				isLoadingAPI: false,
				apiError: apiError
			});
			setLocalError(apiError);
		}
	};

	const handleSubmitRequest = async () => {
		if (!isCancellingChecked && isCancelOrder) {
			setHasError(true);
		} else {
			await handleRequest();
		}
	};

	const isMaintenanceError =
		localError &&
		((isApiErrorResponse(localError) && localError.errors?.[0]?.code === 4002) ||
			("data" in localError &&
				typeof localError?.data !== "string" &&
				isApiErrorResponse(localError.data) &&
				localError.data.errors?.[0]?.code === 4002));

	return (
		<NewOrdersDialogWrapper
			open
			onClose={() => {
				resetApi();
				setDialogAPIInfo(undefined);
				closeDialog();
			}}
			isCancelModal={isCancelOrder}
		>
			<RequestModalWrapper
				isLoading={isLoading}
				isSuccess={isSuccess}
			>
				<DialogTitle
					data-testid="request-dialog-title"
					title={title}
					handleClose={closeDialog}
				/>
				{isMaintenanceError ? (
					<div>
						<ErrorBanner
							errorMessaging={
								<div style={{ flexDirection: "row" }}>
									<div>{NewOrderErrorMaintenanceHeader}</div>
									<div>{GenericOrderErrorMaintenanceSubHeader}</div>
									<div>(4002)</div>
								</div>
							}
						/>
					</div>
				) : (
					<>
						{isCancelOrder && (
							<CancellationAcknowledgementWrapper
								isCancelling={isProcessingRequest}
								hasError={hasError}
							>
								<Alert
									data-testid="cancellation-acknowledgement-banner"
									icon={false}
									severity="error"
								>
									<div>
										<div>{HaltProductionText}</div>
										<div>{YouCannotUndoThisActionText}</div>
									</div>
									<div>{CancellationAcknowledgementText}</div>
								</Alert>
								<FormGroup>
									<FormControlLabel
										data-testid="cancellation-acknowledgement-checkbox"
										control={
											<Checkbox
												checked={isCancellingChecked}
												onClick={toggleCancellation}
											/>
										}
										label={CancellationCheckboxLabel}
									/>
								</FormGroup>
							</CancellationAcknowledgementWrapper>
						)}
						{subtitle && !isProcessingRequest && <RequestSubtitle>{subtitle}</RequestSubtitle>}
						<ProcessingRequestContainer
							hasError={Boolean(localError) && !isLoading}
							isCancelling={isProcessingRequest}
						>
							{isLoading && <h1>{HangTightCancellationText}</h1>}
							{!localError && !isLoading && <h1>{SuccessfulCancellationText}</h1>}
							<ProgressBarWrapper
								hasError={Boolean(localError) && !isLoading}
								isProcessingRequest={isProcessingRequest}
							>
								<div>{requestToRemoveText}</div>
								<LinearProgress
									sx={{ display: isProcessingRequest ? "block" : "none", margin: "4px 0px" }}
									variant={isLoading ? "indeterminate" : "determinate"}
									value={localError ? 2 : 100}
								/>
								{!isLoading && (
									<div>
										<CheckCircleOutline data-testid="request-status-success-svg" />
										{localError ? <div>{RequestFailedText}</div> : <div>{FileSuccessText}</div>}
									</div>
								)}
							</ProgressBarWrapper>
						</ProcessingRequestContainer>
						{!isLoading && !isSuccess && (
							<RequestButtonWrapper isCancelOrder={isCancelOrder}>
								<Button
									fullWidth
									variant="outlined"
									data-testid="cancel-request-button"
									onClick={() => closeDialog()}
									data-id="do-not-cancel-order-button"
									aria-label="do not cancel order"
								>
									{cancelText}
								</Button>
								<Button
									fullWidth
									variant="contained"
									data-testid="confirm-request-cancellation"
									onClick={handleSubmitRequest}
									data-id="request-order-cancellation-button"
									aria-label="request cancellation"
								>
									{confirmText}
								</Button>
							</RequestButtonWrapper>
						)}
					</>
				)}
			</RequestModalWrapper>
		</NewOrdersDialogWrapper>
	);
};
export default RequestDialog;
